const SECRET_EXACT =
  /^(password|pass|secret|token|access[_-]?token|refresh[_-]?token|authorization|auth|cookie|set[_-]?cookie|cvv|cvc|pan|card[_-]?number|security[_-]?code|private[_-]?key|client[_-]?secret|api[_-]?key)$/i;

/** Chaves cujo valor deve ser mascarado (não redacted total). */
const MASK_EMAIL =
  /^(e[_-]?mail|user[_-]?email|customer[_-]?email|emailAddress)$/i;
const MASK_PHONE =
  /^(phone|telefone|mobile|cell|tel|user[_-]?phone|phoneNumber)$/i;
const MASK_NAME =
  /^(name|full[_-]?name|first[_-]?name|last[_-]?name|customer[_-]?name|display[_-]?name)$/i;
const REDACT_ADDRESS =
  /^(address|addr|street|billing[_-]?address|shipping[_-]?address)$/i;

/** Tokens sensíveis embutidos em nomes compostos (userPassword, my_api_key). */
const SENSITIVE_EMBEDDED =
  /(password|passwd|secret|token|authorization|cookie|cvv|cvc|cardnumber|privatekey|apikey|clientsecret|accesstoken|refreshtoken)/i;

const MAX_STRING = 512;
const MAX_ARRAY = 100;
const MAX_DEPTH = 8;

export function maskEmail(value: string): string {
  const at = value.indexOf('@');
  if (at <= 1) return '***';
  return `${value[0]}***${value.slice(at)}`;
}

export function maskPhone(value: string): string {
  const digits = value.replace(/\D/g, '');
  if (digits.length < 4) return '***';
  return `***${digits.slice(-4)}`;
}

export function maskName(value: string): string {
  const trimmed = value.trim();
  if (trimmed.length <= 1) return '*';
  return `${trimmed[0]}***`;
}

function normalizeKey(key: string): string {
  return key
    .trim()
    .toLowerCase()
    .replace(/[-_\s]/g, '');
}

export function isSensitiveKey(key: string): boolean {
  const raw = key.trim();
  if (!raw) return false;
  if (SECRET_EXACT.test(raw)) return true;
  if (MASK_EMAIL.test(raw) || MASK_PHONE.test(raw)) return true;
  if (MASK_NAME.test(raw) || REDACT_ADDRESS.test(raw)) return true;
  const compact = normalizeKey(raw);
  if (SENSITIVE_EMBEDDED.test(compact)) return true;
  return false;
}

function redactValueForKey(key: string, value: unknown): unknown {
  if (typeof value !== 'string') return '[Redacted]';
  if (MASK_EMAIL.test(key) || /email/i.test(key)) return maskEmail(value);
  if (MASK_PHONE.test(key) || /phone|telefone|tel/i.test(key)) {
    return maskPhone(value);
  }
  if (MASK_NAME.test(key)) return maskName(value);
  if (REDACT_ADDRESS.test(key)) return '[Redacted]';
  return '[Redacted]';
}

export function safeClone<T>(value: T): T {
  try {
    return structuredClone(value);
  } catch {
    try {
      return JSON.parse(JSON.stringify(value)) as T;
    } catch {
      return value;
    }
  }
}

export function deepFreeze<T>(value: T): T {
  if (value === null || typeof value !== 'object') return value;
  if (Object.isFrozen(value)) return value;

  if (value instanceof Map) {
    // Snapshot não deve expor Map mutável — congelar entrada já materializada
    for (const [, v] of value) {
      if (v && typeof v === 'object') deepFreeze(v);
    }
    return Object.freeze(value);
  }
  if (value instanceof Set) {
    for (const v of value) {
      if (v && typeof v === 'object') deepFreeze(v);
    }
    return Object.freeze(value);
  }

  for (const key of Reflect.ownKeys(value as object)) {
    const child = (value as Record<string | symbol, unknown>)[key];
    if (child && typeof child === 'object') {
      deepFreeze(child);
    }
  }
  return Object.freeze(value);
}

export function truncateLargeValues(value: unknown, depth = 0): unknown {
  if (depth > MAX_DEPTH) return '[Truncated:max_depth]';
  if (typeof value === 'string') {
    return value.length > MAX_STRING
      ? `${value.slice(0, MAX_STRING)}…[truncated]`
      : value;
  }
  if (Array.isArray(value)) {
    const sliced = value
      .slice(0, MAX_ARRAY)
      .map(v => truncateLargeValues(v, depth + 1));
    if (value.length > MAX_ARRAY) {
      sliced.push(`[Truncated:${value.length - MAX_ARRAY}_more]`);
    }
    return sliced;
  }
  if (value instanceof Map) {
    const out: Record<string, unknown> = {};
    let i = 0;
    for (const [k, v] of value) {
      if (i++ >= MAX_ARRAY) {
        out.__truncated__ = true;
        break;
      }
      out[String(k)] = truncateLargeValues(v, depth + 1);
    }
    return out;
  }
  if (value instanceof Set) {
    return truncateLargeValues([...value], depth);
  }
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      out[k] = truncateLargeValues(v, depth + 1);
    }
    return out;
  }
  return value;
}

/**
 * Sanitização recursiva com proteção a ciclos.
 * Nunca lança; nunca loga o valor removido.
 */
export function recursiveSanitize(
  input: unknown,
  seen = new WeakSet<object>()
): unknown {
  if (input === null || input === undefined) return input;
  if (
    typeof input === 'string' ||
    typeof input === 'number' ||
    typeof input === 'boolean'
  ) {
    return input;
  }
  if (typeof input === 'bigint') return String(input);
  if (typeof input === 'function' || typeof input === 'symbol') {
    return '[Unavailable]';
  }
  if (typeof input !== 'object') return String(input);

  if (seen.has(input as object)) return '[Circular]';
  seen.add(input as object);

  if (Array.isArray(input)) {
    return input.map(item => recursiveSanitize(item, seen));
  }

  if (input instanceof Map) {
    const out: Record<string, unknown> = {};
    for (const [k, v] of input) {
      const key = String(k);
      out[key] = isSensitiveKey(key)
        ? redactValueForKey(key, v)
        : recursiveSanitize(v, seen);
    }
    return out;
  }

  if (input instanceof Set) {
    return [...input].map(item => recursiveSanitize(item, seen));
  }

  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(input as Record<string, unknown>)) {
    if (isSensitiveKey(key)) {
      out[key] = redactValueForKey(key, value);
      continue;
    }
    out[key] = recursiveSanitize(value, seen);
  }
  return out;
}

export function redactSensitiveKeys(
  input: Record<string, unknown>
): Record<string, unknown> {
  return recursiveSanitize(input) as Record<string, unknown>;
}

export function safeSerialize(input: unknown): string {
  try {
    const sanitized = truncateLargeValues(recursiveSanitize(input));
    return JSON.stringify(sanitized);
  } catch {
    return '{"error":"[SerializeFailed]"}';
  }
}

/** Clone + sanitize + freeze pipeline para snapshots. */
export function freezeSanitizedSnapshot<T extends object>(
  input: T
): Readonly<T> {
  const cloned = safeClone(input);
  const sanitized = recursiveSanitize(cloned) as T;
  const truncated = truncateLargeValues(sanitized) as T;
  return deepFreeze(truncated);
}
